import { actionMiddlewares, config, middlewares } from '@dimjs/model';
import { cache } from '@dimjs/utils';
import {
  FabricException,
  FabricExceptionItem,
  FabricMultiExceptionItems,
} from '../models/fabric-exception.js';
import { models } from '../models/index.js';
import { logInfo } from '../utils/log.js';
import { getFullUrlHref } from '../utils/path.js';

const errorId = cache('biz_error_');
const { useStore, getActions } = models;

type UseErrorReturn = [FabricExceptionItem, () => void];
/**
 * Hooks导出一个`models` throw `new FabricError(message:string, type:string)` 出来的异常, 请注意, 此处如果不需要全局捕获异常
 * 此处的异常适合处理全局统一的异常信息, 比如弹出popup/显示一个小界面.
 * 请自己在业务`actions`里面去 `try/catch`
 * @returns [ error:`FabricError | null | undefined`, resetError: `()=> void` ]
 */
export const useException = (): UseErrorReturn => {
  const [state, actions] = useStore('FabricExceptionModel');
  const urlHref = getFullUrlHref();
  logInfo(`state.stack`, state.stack);
  // 快速导航到新route页面, 可能还未产生当前globalError栈
  const statckState = state.stack[urlHref] || {};
  // FIXME: 此处如果当前state未及时清理成功, 取最后一个error, 此处注意Object.keys是否能取最近一个.
  const errorIds = Object.keys(statckState);
  if (errorIds.length > 1) {
    logInfo(
      `Warn: You did not clear latest errors in time, try use "useException()[1]()" to clear`
    );
  }
  const errorId = errorIds.length ? errorIds[errorIds.length - 1] : undefined;
  return [
    errorId ? statckState[errorId] : null,
    () => {
      if (errorId) {
        void actions.resetException(errorId);
      }
    },
  ];
};

/**
 * 当前是否有全局捕获的异常存在, 可能在特殊场景我们需要在页面`hooks.useModelEffectAsync()`
 * 中在页面特定区域显示一个exception 这个时候, 我们需要忽略掉特定action显示的error
 * @returns error: `null 不存在全局异常`
 */
export const useExceptionGlobal = (): FabricMultiExceptionItems => {
  const [error] = useStore('FabricExceptionModel', (state) => {
    logInfo(`state.globalError`, state.globalError);
    // 快速导航到新route页面, 可能还未产生当前globalError栈
    const globalError = state.globalError[getFullUrlHref()] || {};
    return globalError.error;
  });
  return error;
};

export const traceGlobalException = async ({
  urlHref,
  error,
}: {
  urlHref: string;
  error?: FabricMultiExceptionItems;
}) => {
  const actions = getActions('FabricExceptionModel');
  await actions.traceGlobalException({ urlHref, error });
};

/**
 * Add biz try catch capture `middleware`.
 * @param rethrow 是否需要re-throw
 * @param errorHandling customized error handling.
 */
export const addExceptionHandlingMiddleware = (
  rethrow = false,
  errorHandling?: (err: Error | FabricException) => Promise<void>
) => {
  // Find the index of middleware
  const tryCatchMiddlewareIndex = actionMiddlewares.indexOf(
    middlewares.tryCatch
  );
  // Avoid wrong to delete other middleware handlers while invoke `addExceptionHandlingMiddleware` multiple times.
  if (tryCatchMiddlewareIndex !== -1) {
    // Replace default action middlewares `tryCatch`.
    // Note the `@dimjs/models` is `peerDependencies`, please keep only one installed in `node_modules`
    actionMiddlewares.splice(
      tryCatchMiddlewareIndex,
      1,
      async (context, restMiddlewares) => {
        const { next } = context;
        const urlHref = getFullUrlHref();

        // Default is enabled for `production`, Please try/catch in biz `models`
        // If you want to handle speical excetion for specificed `scene`
        if (config.tryCatch.enable) {
          return await next(restMiddlewares).catch((error) => {
            void models.getActions('FabricExceptionModel').setException({
              urlHref,
              id: errorId(),
              exception: error,
            });
            if (typeof errorHandling === 'function') {
              void errorHandling(error);
            }
            if (rethrow) {
              throw error;
            }
          });
        } else {
          return await next(restMiddlewares);
        }
      }
    );
  }
};

export const isFabricException = (exception): exception is FabricException => {
  return exception?.name === 'FabricException';
};
